import { getPaginationUtilityClass } from '@mui/material/Pagination/paginationClasses';
import PaginationItem from '@mui/material/PaginationItem';
import styled from '@mui/material/styles/styled';
import useThemeProps from '@mui/material/styles/useThemeProps';
import clsx from 'clsx';
import PropTypes from 'prop-types';
import React from 'react';
import viewStateVar from 'src/store/viewState';

interface PaginationClasses {
  root?: string;
  ul?: string;
  [key: string]: string | undefined;
}

interface ComposeClassesResult {
  [slot: string]: string;
}

const composeClasses = (
  slots: Record<string, string[]>,
  getUtilityClass: (key: string) => string,
  classes?: PaginationClasses,
): ComposeClassesResult => {
  const result: ComposeClassesResult = {};
  Object.keys(slots).forEach((slot) => {
    result[slot] = slots[slot]
      .map((key) => getUtilityClass(key))
      .filter(Boolean)
      .map((className) => classes?.[className] || className)
      .join(' ');
  });
  return result;
};

interface OwnerState {
  classes?: PaginationClasses;
  variant: 'text' | 'outlined';
  boundaryCount: number;
  color: 'primary' | 'secondary' | 'standard' | string;
  count: number;
  defaultPage: number;
  disabled: boolean;
  hideNextButton: boolean;
  hidePrevButton: boolean;
  shape: 'circular' | 'rounded';
  showFirstButton: boolean;
  showLastButton: boolean;
  siblingCount: number;
  size: 'small' | 'medium' | 'large' | string;
}

const useUtilityClasses = (ownerState: OwnerState): ComposeClassesResult => {
  const { classes, variant } = ownerState;

  const slots: Record<string, string[]> = {
    root: ['root', variant],
    ul: ['ul'],
  };

  return composeClasses(slots, getPaginationUtilityClass, classes);
};

interface PaginationRootProps {
  ownerState: OwnerState;
}

const PaginationRoot = styled('nav', {
  name: 'MuiPagination',
  slot: 'Root',
  overridesResolver: (props: { ownerState: OwnerState }, styles) => {
    const { ownerState } = props;
    return [styles.root, styles[ownerState.variant]];
  },
})<PaginationRootProps>({});

const PaginationUl = styled('ul', {
  name: 'MuiPagination',
  slot: 'Ul',
  overridesResolver: (_, styles) => styles.ul,
})({
  display: 'flex',
  flexWrap: 'nowrap',
  alignItems: 'center',
  padding: 0,
  margin: 0,
  listStyle: 'none',
});

type PaginationItemType = 'page' | 'first' | 'last' | 'next' | 'previous' | 'start-ellipsis' | 'end-ellipsis';

function defaultGetAriaLabel(type: PaginationItemType, page: number, selected: boolean): string {
  if (type === 'page') {
    return `${selected ? '' : 'Go to '}page ${page}`;
  }
  return `Go to ${type} page`;
}

interface PaginationItemData {
  type: PaginationItemType;
  page: number | null;
  selected?: boolean;
  disabled?: boolean;
  onClick: (page: number, event: React.MouseEvent) => void;
}

interface PaginationProps {
  boundaryCount?: number;
  className?: string;
  color?: 'primary' | 'secondary' | 'standard' | string;
  count?: number;
  defaultPage?: number;
  disabled?: boolean;
  getItemAriaLabel?: (type: PaginationItemType, page: number, selected: boolean) => string;
  hideNextButton?: boolean;
  hidePrevButton?: boolean;
  onChange?: (page: number, event?: React.MouseEvent) => void;
  page?: number;
  renderItem?: (item: Record<string, unknown>) => React.ReactNode;
  shape?: 'circular' | 'rounded';
  showFirstButton?: boolean;
  showLastButton?: boolean;
  siblingCount?: number;
  size?: 'small' | 'medium' | 'large' | string;
  variant?: 'text' | 'outlined';
  classes?: PaginationClasses;
  sx?: Record<string, unknown>;
}

const Pagination = React.forwardRef<HTMLElement, PaginationProps>(function Pagination(inProps, ref) {
  const props = useThemeProps({ props: inProps, name: 'MuiPagination' });
  const {
    boundaryCount = 1,
    className,
    color = 'secondary',
    count: _count = 1,
    defaultPage = 1,
    disabled = false,
    getItemAriaLabel = defaultGetAriaLabel,
    hideNextButton = false,
    hidePrevButton = false,
    onChange,
    page: _page,
    renderItem = (item) => <PaginationItem {...item} />,
    shape = 'circular',
    showFirstButton = false,
    showLastButton = false,
    siblingCount = 1,
    size = 'small',
    variant = 'text',
    ...other
  } = props;

  const page = Number(_page);
  const count = Number(_count);
  const itemRestProps = {
    onClick: (currentPage: number, e: React.MouseEvent): void => {
      if (onChange) {
        onChange(currentPage, e);
      }
      viewStateVar({
        ...viewStateVar(),
        isSelectAll: false,
        selectedAssets: [],
      });
    },
  };

  let items: PaginationItemData[] = [];
  if (count <= 5) {
    items = new Array(count).fill(0).map((_, v) => ({ type: 'page' as const, page: v + 1, ...itemRestProps }));
  } else {
    items = [
      { type: 'page' as const, page: 1, ...itemRestProps },
      { type: 'page' as const, page: count, ...itemRestProps },
    ];
    if (page === count) {
      items = [
        ...items,
        { type: 'page' as const, page: count - 1, ...itemRestProps },
        { type: 'page' as const, page: count - 2, ...itemRestProps },
      ];
    } else if (page === 1 || page === 0) {
      items = [
        ...items,
        { type: 'page' as const, page: 2, ...itemRestProps },
        { type: 'page' as const, page: 3, ...itemRestProps },
      ];
    } else {
      items = [
        ...items,
        { type: 'page' as const, page, ...itemRestProps },
        { type: 'page' as const, page: page - 1 === 1 ? page + 2 : page - 1, ...itemRestProps },
        { type: 'page' as const, page: page + 1 === count ? page - 2 : page + 1, ...itemRestProps },
      ];
    }
  }

  let newItems: PaginationItemData[] = [];

  if (count <= 5) {
    newItems = items;
  } else {
    const numberOfPercent = (percent: number, number: number): number => {
      return (percent * 100) / number;
    };
    const percentOfNumber = (number1: number, percent: number): number => {
      return (number1 * percent) / 100;
    };

    let cPagePercent = numberOfPercent(page, count);
    let restPercent = 100 - cPagePercent;
    let addPage1: number;
    let addPage2: number;
    if (cPagePercent < 20 || restPercent < 20) {
      cPagePercent = 35;
      restPercent = 75;
      addPage1 = percentOfNumber(cPagePercent, count);
      addPage2 = percentOfNumber(restPercent, count);
    } else {
      addPage1 = percentOfNumber(cPagePercent / 2, count);
      addPage2 = page + percentOfNumber(restPercent / 2, count);
    }
    const aditionalPages: PaginationItemData[] = [
      { type: 'page' as const, page: Math.round(addPage1), ...itemRestProps },
      { type: 'page' as const, page: Math.round(addPage2), ...itemRestProps },
    ];
    const elipsis: PaginationItemData = {
      type: 'start-ellipsis' as const,
      page: null,
      selected: false,
      disabled: false,
      ...itemRestProps,
    };
    const _newItems = [...items.filter(({ type }) => type === 'page'), ...aditionalPages].sort(
      (a, b) => (a.page ?? 0) - (b.page ?? 0),
    );
    for (const i in _newItems) {
      newItems.push(_newItems[i]);

      if (_newItems[Number(i) + 1]) {
        if ((_newItems[Number(i) + 1].page ?? 0) - (_newItems[Number(i)].page ?? 0) > 1) {
          newItems.push(elipsis);
        }
      }
    }
    newItems = [
      { type: 'previous' as const, disabled: page === 1, page: page - 1, ...itemRestProps },
      ...newItems,
      { type: 'next' as const, disabled: page === count, page: page + 1, ...itemRestProps },
    ];
  }
  newItems = newItems.filter(
    (value, index, self) =>
      value.type !== 'page' || index === self.findIndex((t) => t.page === value.page && t.type === value.type),
  );

  const ownerState: OwnerState = {
    ...props,
    boundaryCount,
    color,
    count,
    defaultPage,
    disabled,
    hideNextButton,
    hidePrevButton,
    shape,
    showFirstButton,
    showLastButton,
    siblingCount,
    size,
    variant,
  };

  const classes = useUtilityClasses(ownerState);

  return (
    <PaginationRoot
      aria-label="pagination navigation"
      className={clsx(classes.root, className)}
      ownerState={ownerState}
      ref={ref}
      {...other}
    >
      <PaginationUl className={classes.ul}>
        {newItems.map((item, index) => (
          // biome-ignore lint/suspicious/noArrayIndexKey: pagination items are positional and have no stable ID
          <li key={index} id={`cyp-page-number-btn-${index}`}>
            {renderItem({
              ...item,
              color,
              sx: {
                color: 'text.primary',
                borderColor: 'divider',
                '&.Mui-selected': {
                  color: 'secondary.contrastText',
                },
              },
              onClick: item.onClick.bind(null, item.page ?? 0),
              selected: page === item.page,
              'aria-label': getItemAriaLabel(item.type, item.page ?? 0, page === item.page),
              shape,
              size,
              variant,
            })}
          </li>
        ))}
      </PaginationUl>
    </PaginationRoot>
  );
});

Pagination.propTypes = {
  boundaryCount: PropTypes.number,
  classes: PropTypes.object,
  className: PropTypes.string,
  color: PropTypes.oneOfType([PropTypes.oneOf(['primary', 'secondary', 'standard']), PropTypes.string]),
  count: PropTypes.number,
  defaultPage: PropTypes.number,
  disabled: PropTypes.bool,
  getItemAriaLabel: PropTypes.func,
  hideNextButton: PropTypes.bool,
  hidePrevButton: PropTypes.bool,
  onChange: PropTypes.func,
  page: PropTypes.number,
  renderItem: PropTypes.func,
  shape: PropTypes.oneOf(['circular', 'rounded']),
  showFirstButton: PropTypes.bool,
  showLastButton: PropTypes.bool,
  siblingCount: PropTypes.number,
  size: PropTypes.oneOfType([PropTypes.oneOf(['small', 'medium', 'large']), PropTypes.string]),
  sx: PropTypes.oneOfType([
    PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.func, PropTypes.object, PropTypes.bool])),
    PropTypes.func,
    PropTypes.object,
  ]),
  variant: PropTypes.oneOfType([PropTypes.oneOf(['outlined', 'text']), PropTypes.string]),
};

export default Pagination;
